import {
  ConsentModule,
  ConsentSignature,
  MostRecentPoliciesResponse,
  SettingsResponse,
  SignedConsentModule,
} from 'features/settings/types/types';
import { WellframeApi } from 'store/api';

export const settingsService = WellframeApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettings: builder.query<SettingsResponse, void>({
      query: () => ({
        url: '/api/v3/settings',
        method: 'GET',
      }),
    }),
    getSignedConsentModules: builder.query<SignedConsentModule[], void>({
      async queryFn(_args, { dispatch }, _extraOptions, baseQuery) {
        const { data: modulesData, error: modulesError } = await baseQuery({
          url: `/api/v3/consent_modules`,
        });
        if (modulesError) return { error: modulesError };

        const { data: signaturesData, error: signaturesError } = await baseQuery({
          url: `/api/v3/consent_signatures`,
        });
        if (signaturesError) return { error: signaturesError };

        // Combine modules and signatures
        const typedModules = (modulesData ?? []) as ConsentModule[];
        const typedSignatures = (signaturesData ?? []) as ConsentSignature[];

        const signedModules: SignedConsentModule[] = typedModules.map((module) => {
          const signature = typedSignatures.find((sig) => sig.module_id === module.id);
          return {
            ...module,
            answered_date: signature?.answered_date ?? null,
            consented: signature?.consented ?? false,
          };
        });

        return { data: signedModules };
      },
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'ConsentModule' as const, id })), 'ConsentModule']
          : ['ConsentModule'],
    }),
    updateConsentModuleSignature: builder.mutation<
      ConsentSignature[],
      { moduleId: number; consented: boolean }
    >({
      query: ({ moduleId, consented }) => ({
        url: `/api/v3/consent_signatures`,
        method: 'PATCH',
        body: {
          consent_signatures: [
            {
              module_id: moduleId,
              answered_date: Math.floor(Date.now() / 1000), // current timestamp in seconds
              consented,
            },
          ],
        },
      }),
    }),
    getMostRecentPolicies: builder.query<MostRecentPoliciesResponse, string>({
      query: (code: string) => ({
        url: `/api/v3/policies/most_recent`,
        method: 'GET',
        params: {
          code,
        },
      }),
    }),
    updateSettings: builder.mutation<
      SettingsResponse,
      {
        settings: {
          privacy_policy: {
            effective_date: string;
          };
        };
      }
    >({
      query: (body) => ({
        url: '/api/v3/settings',
        method: 'PATCH',
        body,
      }),
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useGetSignedConsentModulesQuery,
  useUpdateConsentModuleSignatureMutation,
  useLazyGetMostRecentPoliciesQuery,
  useUpdateSettingsMutation,
} = settingsService;
